/*
This is the main entry point for the demo application.
It is used to start the application in demo mode.
*/

/* eslint-disable @typescript-eslint/no-explicit-any */
import isEqual from 'fast-deep-equal';
import { DBSchema, IDBPDatabase, openDB } from 'idb';
//import { bypass, DefaultBodyType, http, HttpResponse, passthrough } from 'msw';
import { bypass, http, HttpResponse, passthrough } from 'msw';
import { setupWorker } from 'msw/browser';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './app';
import { initializeBigJs } from './big.config';
import './index.css';
import loggedRequestsData from './loggedRequests.json';

initializeBigJs();

type DemoDataRequestJson = {
  method: string;
  url: string;
  queryParams?: any; //Record<string, string> | undefined;
  requestBody?: any; //Record<string, unknown> | DefaultBodyType | undefined;
  responseBody: any; //Record<string, unknown> | Array<Record<string, unknown>>;
};

type DemoIndexedDb = DBSchema & {
  demo: {
    key: string;
    value: DemoDataRequestJson[];
  };
};

async function initDemoDb() {
  const demoDb = await openDB<DemoIndexedDb>('demo', 1, {
    upgrade(db) {
      db.createObjectStore('demo');
    },
  });

  return demoDb;
}

async function getDemoDb() {
  const demoDb = await initDemoDb();
  // sort by key ascending
  const requests = await demoDb.getAll('demo');

  const demoDataRequests = requests.flatMap((set) => Array.from(set.values()));

  const blob = new Blob([JSON.stringify(demoDataRequests, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = 'loggedRequests.json';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

async function addRequestToDemoDb(
  demoDb: IDBPDatabase<DemoIndexedDb>,
  url: string,
  method: string,
  queryParams: any, //  Record<string, string> | undefined,
  requestBody: any, // Record<string, unknown> | DefaultBodyType | undefined,
  responseBody: any, // Record<string, unknown> | Array<Record<string, unknown>>,
) {
  let existingRequest = await demoDb.get('demo', url);
  const incomingRequest = {
    method,
    url,
    queryParams,
    requestBody,
    responseBody,
  };
  if (!existingRequest) {
    existingRequest = [];
  } else if (existingRequest.some((request: DemoDataRequestJson) => isEqual(request, incomingRequest))) {
    return;
  }
  existingRequest.push(incomingRequest);

  await demoDb.put('demo', existingRequest, url);
}

async function recordingHandlers() {
  const handlers = [];
  const demoDb = await initDemoDb();
  await demoDb.clear('demo');

  /*
  This imports the existing loggedRequests.json to help merge new requests with the existing ones.
  */
  for (const request of loggedRequestsData as DemoDataRequestJson[]) {
    await addRequestToDemoDb(
      demoDb,
      request.url,
      request.method,
      request.queryParams,
      request.requestBody,
      request.responseBody,
    );
  }

  const excludePaths = Array.from(
    new Set([
      { path: '/api/sse/stream', methods: ['GET'] },
      { path: '/api/open-banking/auth-link', methods: ['GET'] },
      { path: '/api/fx', methods: ['GET'] },
      { path: '/api/entities/by-type/', methods: ['PATCH', 'POST', 'PUT', 'DELETE'] },
    ]),
  ) as { path: string; methods: string[] }[];

  handlers.push(
    http.all('*', async ({ request }) => {
      const url = new URL(request.url);
      if (
        url.pathname.startsWith(`/api/`) &&
        !excludePaths.some(({ path, methods }) => url.pathname.startsWith(path) && methods.includes(request.method)) &&
        !request.url.includes('?since=')
      ) {
        const requestBody = request.method === 'GET' ? undefined : await request.clone().json();

        const queryParams = url.searchParams.size > 0 ? Object.fromEntries(url.searchParams) : undefined;
        const response = await fetch(bypass(request));
        const responseBody = await response.json();

        await addRequestToDemoDb(demoDb, url.pathname, request.method, queryParams, requestBody, responseBody);

        return HttpResponse.json(responseBody);
      }

      return passthrough();
    }),
  );

  return handlers;
}

async function playbackHandlers() {
  const handlers = loggedRequestsData.map((request: DemoDataRequestJson) => {
    return http.get(request.url, () => {
      return HttpResponse.json(request.responseBody);
    });
  });
  const otherHandlers = [
    http.get('/auth/user-info', () => {
      return HttpResponse.json({
        authenticatedInfo: {
          id: 'usr_01jwvb5j4he06aqw6yspcywgds',
          email: 'demo@seapoint.co',
          emailVerified: true,
          isOrgReady: true,
          name: 'demo@seapoint.co',
          nickname: 'demo',
          picture:
            'https://s.gravatar.com/avatar/ab08bddf23287c4b639f71a352515b95?s=480&r=pg&d=https%3A%2F%2Fcdn.auth0.com%2Favatars%2Fde.png',
          activeOrg: {
            id: 'org_01k1y1x3vmfqfbnc337k9crvrg',
            name: 'Seapoint Finance Limited',
            logoUrl: null,
          },
        },
        isAuthenticated: true,
      });
    }),
    http.get('/api/open-banking/auth-link', () => {
      return HttpResponse.json({
        link: 'https://www.seapoint.co/',
      });
    }),
    http.get('/api/sse/stream', () => {
      return HttpResponse.text('data: []', { headers: { 'Content-Type': 'text/event-stream' } });
    }),
    http.get('/api/fx', ({ request }) => {
      const url = new URL(request.url);
      const base = url.searchParams.get('base');
      const start = url.searchParams.get('start') ?? '2025-01-01';
      const end = url.searchParams.get('end') ?? '2025-06-06';

      function getDatesBetween(startDate: string, endDate: string): string[] {
        const dates: string[] = [];
        const start = new Date(startDate);
        const end = new Date(endDate);

        const current = new Date(start);

        while (current <= end) {
          dates.push(current.toISOString().split('T')[0]);
          current.setDate(current.getDate() + 1);
        }

        return dates;
      }
      function eurRatesForDate() {
        const rates: Record<string, Record<string, number>> = {};
        for (const date of getDatesBetween(start, end)) {
          rates[date] = {
            AED: 3.819415,
            AFN: 77.407827,
            ALL: 99.043351,
            AMD: 411.799602,
            ANG: 1.874768,
            AOA: 949.398369,
            ARS: 1095.889576,
            AUD: 1.654532,
            AWG: 1.87436,
            AZN: 1.767774,
            BAM: 1.953455,
            BBD: 2.079734,
            BDT: 126.389483,
            BGN: 1.955433,
            BHD: 0.391859,
            BIF: 3064.080275,
            BMD: 1.039867,
            BND: 1.403229,
            BOB: 7.188371,
            BRL: 6.037258,
            BSD: 1.039867,
            BTC: 0.00001076,
            BTN: 90.952502,
            BWP: 14.437461,
            BYN: 3.404247,
            BZD: 2.089492,
            CAD: 1.488638,
            CDF: 2970.633196,
            CHF: 0.937412,
            CLF: 0.037145,
            CLP: 1009.579397,
            CNH: 7.57389,
            CNY: 7.561287,
            COP: 4332.272135,
            CRC: 527.865902,
            CUC: 1.039867,
            CUP: 26.77657,
            CVE: 110.274675,
            CZK: 25.173718,
            DJF: 184.849191,
            DKK: 7.459949,
            DOP: 64.391554,
            DZD: 140.561452,
            EGP: 52.32568,
            ERN: 15.598002,
            ETB: 132.628502,
            EUR: 1,
            FJD: 2.401624,
            FKP: 0.831794,
            GBP: 0.831794,
            GEL: 2.953222,
            GGP: 0.831794,
            GHS: 16.021601,
            GIP: 0.831794,
            GMD: 74.870404,
            GNF: 8996.101812,
            GTQ: 8.042424,
            GYD: 217.628729,
            HKD: 8.097157,
            HNL: 26.530866,
            HRK: 7.533075,
            HTG: 136.067988,
            HUF: 406.214123,
            IDR: 16956.362031,
            ILS: 3.686931,
            IMP: 0.831794,
            INR: 90.816397,
            IQD: 1362.49637,
            IRR: 43778.39139,
            ISK: 146.808392,
            JEP: 0.831794,
            JMD: 164.264383,
            JOD: 0.737681,
            JPY: 158.73540382,
            KES: 134.216455,
            KGS: 90.93635,
            KHR: 4181.254755,
            KMF: 491.805031,
            KPW: 935.880101,
            KRW: 1502.895448,
            KWD: 0.32075,
            KYD: 0.866847,
            KZT: 537.716918,
            LAK: 22610.820083,
            LBP: 93133.011762,
            LKR: 310.560083,
            LRD: 205.3249,
            LSL: 19.347992,
            LYD: 5.110327,
            MAD: 10.409539,
            MDL: 19.402399,
            MGA: 4880.763441,
            MKD: 61.511502,
            MMK: 2181.640502,
            MNT: 3533.467315,
            MOP: 8.344284,
            MRU: 41.640975,
            MUR: 48.561777,
            MVR: 16.024347,
            MWK: 1804.287746,
            MXN: 21.40298,
            MYR: 4.598811,
            MZN: 66.45788,
            NAD: 19.347992,
            NGN: 1746.25868,
            NIO: 38.270551,
            NOK: 11.674742,
            NPR: 145.526277,
            NZD: 1.828289,
            OMR: 0.400342,
            PAB: 1.039867,
            PEN: 3.861402,
            PGK: 4.172436,
            PHP: 60.192687,
            PKR: 290.091145,
            PLN: 4.202507,
            PYG: 8196.122681,
            QAR: 3.787929,
            RON: 4.975554,
            RSD: 117.093159,
            RUB: 101.906967,
            RWF: 1449.115224,
            SAR: 3.899667,
            SBD: 8.813063,
            SCR: 14.959717,
            SDG: 624.959934,
            SEK: 11.345633,
            SGD: 1.402623,
            SHP: 0.831794,
            SLL: 21805.486419,
            SOS: 594.40038,
            SRD: 36.504523,
            SSP: 135.453047,
            STD: 23170.103593,
            STN: 24.471097,
            SVC: 9.102161,
            SYP: 13520.347859,
            SZL: 19.358763,
            THB: 34.908328,
            TJS: 11.354071,
            TMT: 3.649933,
            TND: 3.348371,
            TOP: 2.525361,
            TRY: 37.34708,
            TTD: 7.045543,
            TWD: 34.147351,
            TZS: 2658.620181,
            UAH: 43.296844,
            UGX: 3828.467273,
            USD: 1.039867,
            UYU: 45.266818,
            UZS: 13509.125221,
            VES: 61.753902,
            VND: 26176.667419,
            VUV: 123.455064,
            WST: 2.911627,
            XAF: 655.957,
            XAG: 0.03217559,
            XAU: 0.00036242,
            XCD: 2.810292,
            XDR: 0.79785,
            XOF: 655.957,
            XPD: 0.00102425,
            XPF: 119.331742,
            XPT: 0.00103138,
            YER: 258.874728,
            ZAR: 19.288489,
            ZMW: 29.152006,
            ZWL: 334.837103,
          };
        }
        return rates;
      }

      function gbpRatesForDate() {
        const rates: Record<string, Record<string, number>> = {};
        for (const date of getDatesBetween(start, end)) {
          rates[date] = {
            AED: 4.591782,
            AFN: 93.061338,
            ALL: 119.072027,
            AMD: 495.074256,
            ANG: 2.253886,
            AOA: 1141.386948,
            ARS: 1317.501798,
            AUD: 1.989114,
            AWG: 2.253395,
            AZN: 2.125255,
            BAM: 2.348485,
            BBD: 2.5003,
            BDT: 151.948129,
            BGN: 2.350863,
            BHD: 0.471102,
            BIF: 3683.702593,
            BMD: 1.25015,
            BND: 1.686992,
            BOB: 8.642013,
            BRL: 7.25812,
            BSD: 1.25015,
            BTC: 0.000012938131,
            BTN: 109.345036,
            BWP: 17.357023,
            BYN: 4.092658,
            BZD: 2.512032,
            CAD: 1.789673,
            CDF: 3571.358523,
            CHF: 1.126976,
            CLF: 0.044657,
            CLP: 1213.737862,
            CNH: 9.105492,
            CNY: 9.090341,
            COP: 5208.349869,
            CRC: 634.611634,
            CUC: 1.25015,
            CUP: 32.191362,
            CVE: 132.574563,
            CZK: 30.264381,
            DJF: 222.229636,
            DKK: 8.96851,
            DOP: 77.412898,
            DZD: 168.985973,
            EGP: 62.907048,
            ERN: 18.75225,
            ETB: 159.448812,
            EUR: 1.202221,
            FJD: 2.887284,
            FKP: 1,
            GBP: 1,
            GEL: 3.550426,
            GGP: 1,
            GHS: 19.26151,
            GIP: 1,
            GMD: 90.010795,
            GNF: 10815.305279,
            GTQ: 9.668773,
            GYD: 261.637895,
            HKD: 9.734574,
            HNL: 31.895973,
            HRK: 9.056423,
            HTG: 163.583834,
            HUF: 488.359274,
            IDR: 20385.299723,
            ILS: 4.432507,
            IMP: 1,
            INR: 109.181408,
            IQD: 1638.022167,
            IRR: 52631.314916,
            ISK: 176.496177,
            JEP: 1,
            JMD: 197.482141,
            JOD: 0.886856,
            JPY: 190.83508466,
            KES: 161.357882,
            KGS: 109.325617,
            KHR: 5026.793557,
            KMF: 591.258487,
            KPW: 1125.134998,
            KRW: 1806.81293,
            KWD: 0.385612,
            KYD: 1.042142,
            KZT: 646.454736,
            LAK: 27183.209673,
            LBP: 111966.491127,
            LKR: 373.361949,
            LRD: 246.84597,
            LSL: 23.260568,
            LYD: 6.143744,
            MAD: 12.514569,
            MDL: 23.325977,
            MGA: 5867.757803,
            MKD: 73.950439,
            MMK: 2622.814696,
            MNT: 4248.009693,
            MOP: 10.031676,
            MRU: 50.061667,
            MUR: 58.382003,
            MVR: 19.264812,
            MWK: 2169.153172,
            MXN: 25.731118,
            MYR: 5.528789,
            MZN: 79.897079,
            NAD: 23.260568,
            NGN: 2099.389393,
            NIO: 46.009672,
            NOK: 14.035624,
            NPR: 174.954791,
            NZD: 2.198008,
            OMR: 0.4813,
            PAB: 1.25015,
            PEN: 4.64226,
            PGK: 5.016192,
            PHP: 72.364931,
            PKR: 348.753756,
            PLN: 5.052344,
            PYG: 9853.553322,
            QAR: 4.553929,
            RON: 5.981717,
            RSD: 140.77189,
            RUB: 122.514728,
            RWF: 1742.157199,
            SAR: 4.688263,
            SBD: 10.595252,
            SCR: 17.984891,
            SDG: 751.340149,
            SEK: 13.639962,
            SGD: 1.686263,
            SHP: 1,
            SLL: 26215.020383,
            SOS: 714.600802,
            SRD: 43.886516,
            SSP: 162.844539,
            STD: 27855.592225,
            STN: 29.419674,
            SVC: 10.942812,
            SYP: 16254.450274,
            SZL: 23.273517,
            THB: 41.967535,
            TJS: 13.650106,
            TMT: 4.388027,
            TND: 4.025483,
            TOP: 3.036043,
            TRY: 44.899455,
            TTD: 8.470302,
            TWD: 41.052673,
            TZS: 3196.249828,
            UAH: 52.052388,
            UGX: 4602.664929,
            USD: 1.25015,
            UYU: 54.420733,
            UZS: 16240.95818,
            VES: 74.241857,
            VND: 31470.147318,
            VUV: 148.420308,
            WST: 3.50042,
            XAF: 788.605482,
            XAG: 0.03868218,
            XAU: 0.00043571,
            XCD: 3.378593,
            XDR: 0.959192,
            XOF: 788.605482,
            XPD: 0.00123138,
            XPF: 143.463163,
            XPT: 0.00123995,
            YER: 311.224714,
            ZAR: 23.189033,
            ZMW: 35.047163,
            ZWL: 402.548299,
          };
        }
        return rates;
      }

      if (base && base === 'EUR') {
        return HttpResponse.json({
          base: 'EUR',
          rates: eurRatesForDate(),
        });
      } else {
        return HttpResponse.json({
          base: 'GBP',
          rates: gbpRatesForDate(),
        });
      }
    }),
    http.post('/api/evidence/invoice', () => {
      return HttpResponse.json({
        evidence: {
          id: 'evi_01k6fp0bb3fzz9aeqfxd6y4tf4',
        },
        document: {
          fileName: 'Pembr-Invoice.pdf',
          url: '/demo/Pembr-Invoice.pdf',
        },
      });
    }),
    http.get('/api/*', () => {
      return HttpResponse.json({});
    }),
    http.patch('*', () => {
      return HttpResponse.json({});
    }),
    http.post('*', () => {
      return HttpResponse.json({});
    }),
    http.put('*', () => {
      return HttpResponse.json({});
    }),
    http.delete('*', () => {
      return HttpResponse.json({});
    }),
  ];

  handlers.push(...otherHandlers);

  return handlers;
}

async function startMsw() {
  let handlers;
  if (import.meta.env.VITE_DEMO_MODE === 'recording') {
    handlers = await recordingHandlers();
  } else {
    handlers = await playbackHandlers();
  }
  const worker = setupWorker(...handlers);
  await worker.start();
}

startMsw().then(() => {
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      {import.meta.env.VITE_DEMO_MODE === 'recording' && (
        <div className="mt-5 flex justify-center">
          <button className="rounded-md bg-green-500 px-4 py-2 text-white" onClick={() => getDemoDb()}>
            Get Demo DB
          </button>
        </div>
      )}
      <App />
    </StrictMode>,
  );
});
